import { useState } from 'react'
import { ConeTecMark } from './icons'
import { useHubLang } from '../lib/i18n'
import { HOME } from '../lib/hubCopy'
import type { PostOriginal } from '../lib/feed'

/**
 * "Translated by One · Show original" — ONE rendering, for every surface that
 * shows words a machine translated (111 posts, 112 Wiki One answers).
 *
 * translation_status='machine' means One wrote these words from somebody
 * else's. Teal and the mark in a ring is how this hub says A MACHINE WROTE
 * THIS, so the line wears both, and it sits ABOVE the words it wrote, before
 * anybody reads them. 011's rule is that machine text renders visibly marked;
 * a second copy of this markup would drift the first time either surface
 * changed it, which is the "two of anything" this hub keeps refusing.
 *
 * The original is fetched on the one press that needs it, so a list of thirty
 * rows makes no extra calls. The caller supplies the loader (post_original,
 * wiki_original) because only the caller knows which kind of thing it holds.
 */
/** Title, body and language of the words a translation was made from. */
export type SourceText = PostOriginal

export type OriginalState = {
  machine: boolean
  showingOriginal: boolean
  original: SourceText | null
  busy: boolean
  failed: boolean
  toggle: () => Promise<void>
}

export function useOriginal(
  machine: boolean,
  load: () => Promise<SourceText | null>,
): OriginalState {
  const [original, setOriginal] = useState<SourceText | null>(null)
  const [show, setShow] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)

  async function toggle() {
    if (show) { setShow(false); return }
    setFailed(false)
    if (original) { setShow(true); return }
    setBusy(true)
    try {
      const o = await load()
      if (!o) { setFailed(true); return }
      setOriginal(o)
      setShow(true)
    } catch {
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }

  return {
    machine,
    showingOriginal: machine && show && original !== null,
    original,
    busy,
    failed,
    toggle,
  }
}

/** Renders nothing unless a machine wrote the words on screen. */
export function TranslatedLine({ state }: { state: OriginalState }) {
  const h = HOME[useHubLang()]
  if (!state.machine) return null
  const showing = state.showingOriginal && state.original !== null
  return (
    <p className="post__translated">
      <span className="post__translated-mark" aria-hidden="true">
        <ConeTecMark size={12} />
      </span>
      <span title={h.translatedTitle}>
        {showing ? h.originalIn(state.original!.locale) : h.translatedByOne}
      </span>
      <span aria-hidden="true">·</span>
      <button
        type="button"
        className="post__translated-toggle"
        aria-pressed={showing}
        disabled={state.busy}
        onClick={() => void state.toggle()}
      >
        {showing ? h.showTranslation : h.showOriginal}
      </button>
      {state.failed && <span className="post__translated-fail">{h.originalFailed}</span>}
    </p>
  )
}
